import React, { useCallback, useRef, useState } from "react";
import toBoolean from "../../lib/iprs-react-library/src/util/toBoolean";

/**
 * Drag-and-drop file upload override for the `fileupload` control type.
 *
 * Opt-in per form: the field definition in the form spec must set
 * `"UseDragAndDrop": true`. When it isn't set, the library's default
 * SimpleFileUpload (a plain <input type=file>) is used instead.
 *
 * Behaviour vs the library's SimpleFileUpload:
 *  - drag-and-drop zone (AC2) that is also clickable / keyboard accessible
 *  - lists the uploaded file names (AC3) with a remove (✕) control per file
 *  - for `multiple` fields, successive drops/selections ACCUMULATE rather than
 *    replace; single-file fields replace as before
 *  - upload-in-progress and error feedback
 * No `accept` filter is applied, so all currently-accepted file types remain
 * accepted (AC4).
 *
 * Upload path matches SimpleFileUpload: each new batch is sent (as the native
 * FileList) via `api.uploadFiles`; the form value passed up through
 * `handleChange` is the merged array of upload results across all batches.
 *
 * @param {object} props as supplied by UnvalidatedForm.getPropsForComponent
 */
const sendOnChangeEventToParent = (handleChange, value, name) => {
    handleChange({
        preventDefault: () => true,
        target: { name, value }
    });
};

const DragAndDropFileUpload = ({
    handleChange,
    api,
    name,
    text,
    noFileUpload = false,
    multiple,
    required = false
}) => {
    if (
        !noFileUpload &&
        (typeof api !== "object" || typeof api.uploadFiles !== "function")
    ) {
        throw new Error(
            "When noFileUpload is not set, an instance of API must be provided with which to upload files"
        );
    }

    const multipleBool = toBoolean(multiple);

    const [isDragging, setIsDragging] = useState(false);
    // Each item: { id, name, result }. `result` is the per-file API upload
    // result (or the File itself when noFileUpload is set).
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("idle"); // idle | uploading | error
    const [error, setError] = useState(null);

    // Mirror items in a ref so async upload/remove handlers can merge against
    // the latest list without stale closures or functional-setState side effects.
    const itemsRef = useRef([]);
    const nextIdRef = useRef(0);

    const commitItems = useCallback(
        newItems => {
            itemsRef.current = newItems;
            setItems(newItems);
            sendOnChangeEventToParent(
                handleChange,
                newItems.map(it => it.result),
                name
            );
        },
        [handleChange, name]
    );

    const addResults = useCallback(
        (names, results) => {
            const newItems = results.map((result, i) => ({
                id: nextIdRef.current++,
                name: names[i] ?? result?.FileName ?? "file",
                result
            }));
            const merged = multipleBool
                ? [...itemsRef.current, ...newItems]
                : newItems;
            commitItems(merged);
        },
        [commitItems, multipleBool]
    );

    const removeItem = useCallback(
        id => commitItems(itemsRef.current.filter(it => it.id !== id)),
        [commitItems]
    );

    const processFiles = useCallback(
        // `fileList` is a native FileList. The API iterates it with
        // files.item(i), so it must be passed through unchanged (do NOT convert
        // to an array). We only build an array to read the names for display.
        fileList => {
            if (!fileList || 0 === fileList.length) {
                return;
            }
            const names = Array.from(fileList).map(f => f.name);

            if (noFileUpload) {
                addResults(names, Array.from(fileList));
            } else {
                setStatus("uploading");
                setError(null);
                api.uploadFiles(fileList)
                    .then(r => r.apiResult)
                    .then(uploadedFiles => {
                        setStatus("idle");
                        addResults(names, uploadedFiles);
                    })
                    .catch(e => {
                        setStatus("error");
                        setError("File upload failed. Please try again.");
                        console.log("FAIL", e);
                    });
            }
        },
        [api, noFileUpload, addResults]
    );

    const onDrop = useCallback(
        e => {
            e.preventDefault();
            setIsDragging(false);
            const files = e.dataTransfer?.files;
            if (files && files.length) {
                processFiles(files);
            }
        },
        [processFiles]
    );

    const onDragOver = e => {
        e.preventDefault();
        setIsDragging(true);
    };

    const onDragLeave = e => {
        e.preventDefault();
        setIsDragging(false);
    };

    const onInputChange = e => {
        e.preventDefault();
        const files = e.target.files;
        if (files && files.length) {
            processFiles(files);
        }
        // Reset so selecting the same file again still fires a change event.
        e.target.value = "";
    };

    const zoneClass =
        "form__dropzone" + (isDragging ? " form__dropzone--active" : "");

    return (
        <fieldset className="form__field form__field--full-width">
            <legend className="form__dropzone-legend">
                {text ?? "File Upload"}
                {required && <span style={{ color: "red" }}> *</span>}
            </legend>
            <div
                className={zoneClass}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
            >
                <label htmlFor={name} className="form__dropzone-label">
                    <span className="form__dropzone-instruction">
                        Drag and drop {multipleBool ? "files" : "a file"} here,
                        or <span className="form__dropzone-browse">click to browse</span>
                    </span>
                    <input
                        id={name}
                        type="file"
                        className="form__dropzone-input"
                        onChange={onInputChange}
                        name={name}
                        multiple={multipleBool}
                    />
                </label>

                {"uploading" === status && (
                    <p className="form__dropzone-status">Uploading…</p>
                )}
                {"error" === status && (
                    <p className="form__dropzone-error">{error}</p>
                )}
                {items.length > 0 && (
                    <ul className="form__dropzone-files">
                        {items.map(item => (
                            <li key={item.id} className="form__dropzone-file">
                                <span className="form__dropzone-file-name">
                                    {item.name}
                                </span>
                                <button
                                    type="button"
                                    className="form__dropzone-remove"
                                    aria-label={`Remove ${item.name}`}
                                    onClick={() => removeItem(item.id)}
                                >
                                    ✕
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </fieldset>
    );
};

export default DragAndDropFileUpload;
